import { useMemo } from "react"
import { useGaugeState } from "@mui/x-charts"

import type { GaugeConfigTicks } from "types/gaugePlot.types"

import { getLabelValues, TICK_COUNT } from "./helpers"

interface MuiGaugeTicksProps {
  ticks: GaugeConfigTicks
  size: number
  startAngleRad: number
  endAngleRad: number
  /** Domain bounds, used for tick label computation. */
  domainMin: number
  domainMax: number
}

const MuiGaugeTicks = ({
  ticks,
  size,
  startAngleRad,
  endAngleRad,
  domainMin,
  domainMax,
}: MuiGaugeTicksProps) => {
  const { outerRadius, cx, cy } = useGaugeState()
  const { valueInterval, labelCount, prefix, suffix, fill } = ticks

  const totalRange = domainMax - domainMin

  const dotRadius = outerRadius * 1.15
  const textRadius = outerRadius * 1.28
  const fontSize = size / 28

  const angleForRatio = (ratio: number) =>
    startAngleRad + ratio * (endAngleRad - startAngleRad)

  const labelValues = useMemo(
    () => getLabelValues(domainMin, domainMax, valueInterval, labelCount),
    [domainMin, domainMax, valueInterval, labelCount],
  )

  return (
    <g>
      {Array.from({ length: TICK_COUNT }, (_, i) => {
        const ratio = i / (TICK_COUNT - 1)
        const angle = angleForRatio(ratio)
        const dotX = cx + dotRadius * Math.sin(angle)
        const dotY = cy - dotRadius * Math.cos(angle)

        return <circle key={`dot-${i}`} cx={dotX} cy={dotY} r={0.5} fill={fill} />
      })}

      {labelValues.map((value) => {
        const ratio = totalRange === 0 ? 0 : (value - domainMin) / totalRange
        const angle = angleForRatio(ratio)
        const textX = cx + textRadius * Math.sin(angle)
        const textY = cy - textRadius * Math.cos(angle)

        return (
          <text
            key={`label-${value}`}
            x={textX}
            y={textY}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={fill}
            fontSize={fontSize}
          >
            {prefix ? `${prefix} ` : ""}
            {value}
            {suffix ? ` ${suffix}` : ""}
          </text>
        )
      })}
    </g>
  )
}

export default MuiGaugeTicks
